import { Component, computed, effect, input, signal, viewChild, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
    EmployeeAutocompleteComponent,
    OptionStatus,
} from '@app/components/platform/employee-autocomplete/employee-autocomplete.component';
import { TaxReturnFormsTables } from '@app/modules/payroll/components/tax-returns/tax-return-forms-tables/tax-return-forms-tables.component';
import { PlatformModule } from '@app/platform.module';
import { AuthService } from '@app/services';
import { BaseForm } from '@forms/base.form';
import { Employee } from '@models/employee/employee.model';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'app-add-amended-tax-form',
    standalone: true,
    imports: [PlatformModule, TranslateModule, FormsModule],
    templateUrl: './add-amended-tax.template.html',
})
export class AddAmendedTaxForm extends BaseForm {
    RETURN_TYPES = ['T4', 'RL1'] as const;

    employeesInAmendment = input<TaxReturnFormsTables['employeesInAmendment']>();
    hasQcPayrollFlag = input<boolean>();
    submittedReturn = input<(typeof this.RETURN_TYPES)[number] | undefined>();
    selectEmployee = viewChild(EmployeeAutocompleteComponent);
    /**
     * The employee to be added to the amendment
     */
    addEmployee = signal<Employee | undefined>(undefined);
    /**
     * The tax packages to create an amendment for the employee (controls both selection and disabled value)
     */
    packages: Record<(typeof this.RETURN_TYPES)[number], { isSelected: WritableSignal<boolean>; isDisabled: boolean }> =
        {
            T4: { isSelected: signal(true), isDisabled: false },
            RL1: { isSelected: signal(true), isDisabled: false },
        };
    customEmployeeQuery = Employee.param('company', this.auth.company.id)
        .where('onPayroll', true)
        .where('status', ['active', 'onboarding', 'terminated', 'on leave'])
        .orderBy('status', 'ASC');

    /**
     * The form can only be submitted if an employee is selected and at least one return type
     */
    canSubmit = computed(
        () => !!this.addEmployee() && this.RETURN_TYPES.some((returnType) => this.packages[returnType].isSelected())
    );

    excludedEmployeeIds = computed(() => {
        const employeesInAmendment = this.employeesInAmendment();
        if (!employeesInAmendment) {
            return [];
        }
        if (this.hasQcPayrollFlag()) {
            // If RL1s are enabled we will only exclude employees that are in BOTH RL1s and T4s
            return Object.entries(employeesInAmendment)
                .filter(([, alreadyInForms]) =>
                    this.RETURN_TYPES.filter((type) => type !== this.submittedReturn()) // We don't care about employees that are already in a submitted form so we filter out here (ie. if T4 is submitted, we only check RL-1 employees)
                        .every((returnType) => alreadyInForms[returnType])
                )
                .map(([key]) => key);
        }
        // If RL1s aren't enabled then we exclude every employee that's in employeesInAmendment since it will only contain employees in the T4
        return Object.keys(employeesInAmendment);
    });

    // If the user selects an employee that already exists in a specific return type, we deselect the checkbox for that return type
    private deselectSelectedEmployees = effect(
        () => {
            // This effect isn't necessary if RL-1s aren't enabled since the checkboxes are not present
            if (!this.hasQcPayrollFlag()) {
                this.deselectSelectedEmployees.destroy();
                return;
            }

            const submittedReturn = this.submittedReturn();
            // Even before an employee is selected we should disable a form type checkbox if it's already submitted
            if (submittedReturn) {
                this.packages[submittedReturn].isSelected.set(false);
                this.packages[submittedReturn].isDisabled = true;
            }

            const employeeToBeAdded = this.addEmployee();
            const employeesInAmendment = this.employeesInAmendment();
            if (!employeeToBeAdded || !employeesInAmendment) {
                return;
            }

            const employeesForms = employeesInAmendment[employeeToBeAdded.id];
            if (employeesForms) {
                // If the employee is in an amendment already or in a submitted form
                this.RETURN_TYPES.forEach((returnType) => {
                    if (employeesForms[returnType] || this.submittedReturn() === returnType) {
                        // Disable and unselect the checkbox for that return type
                        this.packages[returnType].isSelected.set(false);
                        this.packages[returnType].isDisabled = true;
                    } else {
                        // Enable and select the checkbox for all other return types
                        this.packages[returnType].isSelected.set(true);
                        this.packages[returnType].isDisabled = false;
                    }
                });
            } else {
                // If the employee isn't already in any amendments then we can enable and select all checkboxes by default (the user can deselect any they don't require)
                this.RETURN_TYPES.forEach((returnType) => {
                    // However, we still want to make sure submitted forms are disabled and unselected
                    if (this.submittedReturn() === returnType) {
                        this.packages[returnType].isSelected.set(false);
                        this.packages[returnType].isDisabled = true;
                    } else {
                        this.packages[returnType].isSelected.set(true);
                        this.packages[returnType].isDisabled = false;
                    }
                });
            }
        },
        { allowSignalWrites: true }
    );

    constructor(private auth: AuthService) {
        super();
    }

    employeeStatusDeterminer: (employee: Employee) => OptionStatus | null = (employee: Employee) => {
        if (employee.status === 'terminated') {
            return { type: 'danger', label: 'Terminated' };
        }
        return null;
    };

    clear(): void {
        this.selectEmployee()?.clear();
    }
}
